import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { DefaultOptionType } from 'antd/es/select';
import { debounce } from 'lodash';
import {
  getDimensionCollection,
  getProjectCollection,
  getCostTypes,
  getCompanyCardCollection,
  getXglCollection,
  getDimension,
  getProject,
  getCompanyCard,
  getXgl,
  getUsers,
} from '../../../api/api';
import {
  DEFAULT_PAGE_OFFSET,
  DEFAULT_PAGE_SIZE,
  MAX_SEARCH_PAGE_SIZE,
} from 'src/utils/constants';
import { UseExpenseDataProps } from '../types';

/**
 * Creates initial fetch parameters for API calls
 */
const createFetchParams = (clientId: string): API.FetchParams => ({
  client_id: clientId,
  search: '',
  limit: DEFAULT_PAGE_SIZE,
  offset: DEFAULT_PAGE_OFFSET,
});

/**
 * Creates a debounced search handler for updating fetch parameters
 */
const createDebounceHandler = (
  setParams: React.Dispatch<React.SetStateAction<API.FetchParams>>
) => {
  return debounce((params: API.FetchParams) => {
    setParams((prev) => ({
      ...prev,
      ...params,
      limit:
        params.limit ||
        (params.search === '' ? DEFAULT_PAGE_SIZE : MAX_SEARCH_PAGE_SIZE),
    }));
  }, 500);
};

/**
 * Transforms API response items to select options
 */
const transformToSelectOption = (
  item: any,
  labelKey: string,
  valueKey = 'id',
  disabledKey = 'active'
) => ({
  label: item[labelKey],
  value: item[valueKey],
  disabled: disabledKey === 'active' ? !item.active : item[disabledKey],
});

/**
 * Generic async fetch wrapper with loading state management and abort signal support
 */
const withLoadingState = async <T>(
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  fetchFn: () => Promise<T>,
  errorMessage: string,
  signal?: AbortSignal
): Promise<T | null> => {
  setLoading(true);
  try {
    return await fetchFn();
  } catch (error) {
    if (signal?.aborted || (error as Error).name === 'CanceledError') {
      // Don't set loading to false for aborted requests
      // Let the new request manage loading state
      return null;
    }
    console.error(errorMessage, error);
    return null;
  } finally {
    // Only set loading to false if not aborted
    if (!signal?.aborted) {
      setLoading(false);
    }
  }
};

/**
 * Custom React hook for managing expense form data fetching and caching
 *
 * Provides comprehensive data management for expense form fields including:
 * - Dropdown options with debounced search
 * - Loading states for all data sources
 * - Individual item fetching by ID
 * - Client-filtered results
 * - Optimized API calls with pagination
 *
 * Data Sources:
 * - Dimensions (organizational units)
 * - Projects (expense categorization)
 * - Cost Types (expense classification)
 * - Company Cards (payment methods)
 * - XGL (General Ledger codes)
 * - Users (expense owners)
 *
 * @param props - UseExpenseDataProps containing authentication and client context
 * @returns Object containing all data, loading states, and fetch handlers
 * and manages loading and data states for all entities. The hook also handles initialization logic
 * based on the expense status (e.g., fetching specific items for sent/failed expenses).
 *
 * @param props - Configuration object for the hook.
 * @param props.accessToken - The access token for API authentication.
 * @param props.status - The current status of the expense (e.g., 'sent', 'failed', 'draft').
 * @param props.recordToEdit - The expense record being edited, if any.
 * @param props.clientId - The client ID for filtering API requests.
 *
 * @returns An object containing:
 * - Loading states for each entity.
 * - Data arrays for each entity (dimensions, projects, etc.).
 * - Fetch functions for individual cost types.
 * - Debounced search handlers for each entity.
 */
export const useExpenseData = ({
  accessToken,
  status,
  recordToEdit,
  clientId,
}: UseExpenseDataProps) => {
  const [dimensionsLoading, setDimensionsLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [costTypesLoading, setCostTypesLoading] = useState(false);
  const [companyCardsLoading, setCompanyCardsLoading] = useState(false);
  const [xglListLoading, setXglListLoading] = useState(false);
  const [userListLoading, setUserListLoading] = useState(false);
  const [initializationLoading, setInitializationLoading] = useState(false);
  const [dimensions, setDimensions] = useState<DefaultOptionType[]>([]);
  const [projects, setProjects] = useState<DefaultOptionType[]>([]);
  const [costTypes, setCostTypes] = useState<DefaultOptionType[]>([]);
  const [companyCards, setCompanyCards] = useState<DefaultOptionType[]>([]);
  const [xglList, setXglList] = useState<DefaultOptionType[]>([]);
  const [userList, setUserList] = useState<DefaultOptionType[]>([]);

  // Pagination response state for each data type
  const [dimensionsPagination, setDimensionsPagination] = useState<
    API.PaginationResponse | undefined
  >();
  const [projectsPagination, setProjectsPagination] = useState<
    API.PaginationResponse | undefined
  >();
  const [costTypesPagination, setCostTypesPagination] = useState<
    API.PaginationResponse | undefined
  >();
  const [companyCardsPagination, setCompanyCardsPagination] = useState<
    API.PaginationResponse | undefined
  >();
  const [xglPagination, setXglPagination] = useState<
    API.PaginationResponse | undefined
  >();
  const [fetchDimensionsParams, setFetchDimensionsParams] =
    useState<API.FetchParams>(() => createFetchParams(clientId));
  const [fetchProjectsParams, setFetchProjectsParams] =
    useState<API.FetchParams>(() => createFetchParams(clientId));
  const [fetchCostTypesParams, setFetchCostTypesParams] =
    useState<API.FetchParams>(() => createFetchParams(clientId));
  const [fetchCompanyCardsParams, setFetchCompanyCardsParams] =
    useState<API.FetchParams>(() => createFetchParams(clientId));
  const [fetchXglListParams, setFetchXglListParams] = useState<API.FetchParams>(
    () => createFetchParams(clientId)
  );

  const hasFetched = useRef(false);
  const pendingCalls = useRef({
    dimensions: false,
    projects: false,
    costTypes: false,
    companyCards: false,
    xgl: false,
  });

  // AbortController refs for cancelling search requests
  const abortControllers = useRef<Record<string, AbortController | null>>({
    dimensions: null,
    projects: null,
    costTypes: null,
    companyCards: null,
    xgl: null,
  });

  /* Fetch Dimensions Block */
  const fetchDimensionCollection = useCallback(async () => {
    // Cancel previous request if exists
    if (abortControllers.current.dimensions) {
      abortControllers.current.dimensions.abort();
    }

    // Create new AbortController for this request
    abortControllers.current.dimensions = new AbortController();
    const signal = abortControllers.current.dimensions.signal;

    try {
      const response = await withLoadingState(
        setDimensionsLoading,
        () =>
          getDimensionCollection(accessToken, fetchDimensionsParams, {
            signal,
          }),
        'Error fetching dimensions:',
        signal
      );

      if (response && !signal.aborted) {
        const filteredResponse = response.data.items.filter(
          (x: API.DimensionItem) =>
            x.active || x.id === recordToEdit?.dimension_id
        );
        setDimensions(
          filteredResponse.map((item: API.DimensionItem) =>
            transformToSelectOption(item, 'description')
          )
        );
        // Update pagination state
        setDimensionsPagination({
          total: response.data.total,
          page: response.data.page,
          size: response.data.size,
          pages: response.data.pages,
        });
      }
    } finally {
      pendingCalls.current.dimensions = false;
    }
  }, [accessToken, fetchDimensionsParams, recordToEdit?.dimension_id]);

  const fetchDimension = useCallback(async () => {
    if (!recordToEdit?.dimension_id) return;

    const response = await withLoadingState(
      setDimensionsLoading,
      () => getDimension(accessToken, recordToEdit.dimension_id),
      'Error fetching dimension:'
    );

    if (response) {
      setDimensions((prev) => [
        ...prev,
        transformToSelectOption(response.data, 'description'),
      ]);
    }
  }, [accessToken, recordToEdit?.dimension_id]);

  const debouncedFetchDimensions = useMemo(
    () => createDebounceHandler(setFetchDimensionsParams),
    []
  );

  /* Fetch Projects Block */
  const fetchProjectCollection = useCallback(async () => {
    // Cancel previous request if exists
    if (abortControllers.current.projects) {
      abortControllers.current.projects.abort();
    }

    // Create new AbortController for this request
    abortControllers.current.projects = new AbortController();
    const signal = abortControllers.current.projects.signal;

    try {
      const response = await withLoadingState(
        setProjectsLoading,
        () =>
          getProjectCollection(accessToken, fetchProjectsParams, { signal }),
        'Error fetching projects:',
        signal
      );

      if (response && !signal.aborted) {
        const filteredResponse = response.data.items.filter(
          (x: API.DimensionItem) =>
            x.active || x.id === recordToEdit?.project_id
        );
        setProjects(
          filteredResponse.map((item: API.DimensionItem) =>
            transformToSelectOption(item, 'description')
          )
        );
        // Update pagination state
        setProjectsPagination({
          total: response.data.total,
          page: response.data.page,
          size: response.data.size,
          pages: response.data.pages,
        });
      }
    } finally {
      pendingCalls.current.projects = false;
    }
  }, [accessToken, fetchProjectsParams, recordToEdit?.project_id]);

  const fetchProject = useCallback(async () => {
    if (!recordToEdit?.project_id) return;

    const response = await withLoadingState(
      setProjectsLoading,
      () => getProject(accessToken, recordToEdit.project_id),
      'Error fetching project:'
    );

    if (response) {
      setProjects((prev) => [
        ...prev,
        transformToSelectOption(response.data, 'description'),
      ]);
    }
  }, [accessToken, recordToEdit?.project_id]);

  const debouncedFetchProjects = useMemo(
    () => createDebounceHandler(setFetchProjectsParams),
    []
  );

  /* Fetch Cost Types Block */
  const fetchCostTypeCollection = useCallback(async () => {
    // Cancel previous request if exists
    if (abortControllers.current.costTypes) {
      abortControllers.current.costTypes.abort();
    }

    // Create new AbortController for this request
    abortControllers.current.costTypes = new AbortController();
    const signal = abortControllers.current.costTypes.signal;

    try {
      const response = await withLoadingState(
        setCostTypesLoading,
        () => getCostTypes(accessToken, fetchCostTypesParams, { signal }),
        'Error fetching cost types:',
        signal
      );

      if (response && !signal.aborted) {
        const filteredResponse = response.data.items.filter(
          (x: API.CostType) => x.active
        );
        setCostTypes(
          filteredResponse.map((item: API.CostType) => ({
            label: item.description,
            value: item.id,
          }))
        );
        // Update pagination state
        setCostTypesPagination({
          total: response.data.total,
          page: response.data.page,
          size: response.data.size,
          pages: response.data.pages,
        });
      }
    } finally {
      pendingCalls.current.costTypes = false;
    }
  }, [accessToken, fetchCostTypesParams]);

  const debouncedFetchCostTypes = useMemo(
    () => createDebounceHandler(setFetchCostTypesParams),
    []
  );

  /* Fetch Company Cards Block */
  const fetchCompanyCardCollection = useCallback(async () => {
    // Prevent duplicate calls
    if (companyCardsLoading || pendingCalls.current.companyCards) return;

    // Cancel previous request if exists
    if (abortControllers.current.companyCards) {
      abortControllers.current.companyCards.abort();
    }

    // Create new AbortController for this request
    abortControllers.current.companyCards = new AbortController();
    const signal = abortControllers.current.companyCards.signal;

    pendingCalls.current.companyCards = true;

    try {
      const response = await withLoadingState(
        setCompanyCardsLoading,
        () =>
          getCompanyCardCollection(accessToken, fetchCompanyCardsParams, {
            signal,
          }),
        'Error fetching company cards:',
        signal
      );

      if (response && !signal.aborted) {
        const filteredResponse = response.data.items.filter(
          (x: API.CompanyCard) =>
            x.active || x.id === recordToEdit?.company_card_id
        );
        setCompanyCards(
          filteredResponse.map((item: API.CompanyCard) => ({
            label: item.card_name,
            value: item.id,
            disabled: !item.active,
          }))
        );
        // Update pagination state
        setCompanyCardsPagination({
          total: response.data.total,
          page: response.data.page,
          size: response.data.size,
          pages: response.data.pages,
        });
      }
    } finally {
      pendingCalls.current.companyCards = false;
    }
  }, [accessToken, fetchCompanyCardsParams, recordToEdit?.company_card_id]);

  const fetchCompanyCard = useCallback(async () => {
    if (recordToEdit?.company_card_id === undefined) return;

    const response = await withLoadingState(
      setCompanyCardsLoading,
      () => getCompanyCard(accessToken, recordToEdit.company_card_id),
      'Error fetching company card:'
    );

    if (response) {
      setCompanyCards((prev) => [
        ...prev,
        transformToSelectOption(response.data, 'card_name'),
      ]);
    }
  }, [accessToken, recordToEdit?.company_card_id]);

  const debouncedFetchCompanyCards = useMemo(
    () => createDebounceHandler(setFetchCompanyCardsParams),
    []
  );

  /* Fetch XGL Block */
  const fetchXglCollection = useCallback(async () => {
    // Cancel previous request if exists
    if (abortControllers.current.xgl) {
      abortControllers.current.xgl.abort();
    }

    // Create new AbortController for this request
    abortControllers.current.xgl = new AbortController();
    const signal = abortControllers.current.xgl.signal;

    try {
      const response = await withLoadingState(
        setXglListLoading,
        () => getXglCollection(accessToken, fetchXglListParams, { signal }),
        'Error fetching XGL:',
        signal
      );

      if (response && !signal.aborted) {
        const filteredResponse = response.data.items.filter(
          (x: API.DimensionItem) => x.active || x.id === recordToEdit?.xgl_id
        );
        setXglList(
          filteredResponse.map((item: API.DimensionItem) => ({
            label: item.description,
            value: item.id,
            disabled: !item.active,
          }))
        );
        // Update pagination state
        setXglPagination({
          total: response.data.total,
          page: response.data.page,
          size: response.data.size,
          pages: response.data.pages,
        });
      }
    } finally {
      pendingCalls.current.xgl = false;
    }
  }, [accessToken, fetchXglListParams, recordToEdit?.xgl_id]);

  const fetchXgl = useCallback(async () => {
    if (recordToEdit?.xgl_id === undefined) return;

    const response = await withLoadingState(
      setXglListLoading,
      () => getXgl(accessToken, recordToEdit.xgl_id),
      'Error fetching XGL:'
    );

    if (response) {
      setXglList((prev) => [
        ...prev,
        transformToSelectOption(response.data, 'description'),
      ]);
    }
  }, [accessToken, recordToEdit?.xgl_id]);

  const debouncedFetchXgl = useMemo(
    () => createDebounceHandler(setFetchXglListParams),
    []
  );

  /* Fetch Users Block */
  const fetchUserList = useCallback(async () => {
    const response = await withLoadingState(
      setUserListLoading,
      () => getUsers(accessToken),
      'Error fetching users:'
    );

    if (response) {
      setUserList(
        response.data.map((item: API.TenantUser) => ({
          label: `${item.first_name} ${item.last_name} - ${item.email}`,
          value: item.email,
          firstName: item.first_name,
          lastName: item.last_name,
          email: item.email,
        }))
      );
    }
  }, [accessToken]);

  // Effects to trigger fetch when search parameters change (only after initialization)
  useEffect(() => {
    if (hasFetched.current) {
      fetchDimensionCollection();
    }
  }, [fetchDimensionsParams, fetchDimensionCollection]);

  useEffect(() => {
    if (hasFetched.current) {
      fetchProjectCollection();
    }
  }, [fetchProjectsParams, fetchProjectCollection]);

  useEffect(() => {
    if (hasFetched.current) {
      fetchCostTypeCollection();
    }
  }, [fetchCostTypesParams, fetchCostTypeCollection]);

  useEffect(() => {
    if (hasFetched.current) {
      fetchCompanyCardCollection();
    }
  }, [fetchCompanyCardsParams, fetchCompanyCardCollection]);

  useEffect(() => {
    if (hasFetched.current) {
      fetchXglCollection();
    }
  }, [fetchXglListParams, fetchXglCollection]);

  // Initialization effect - runs only once when component mounts
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const initializeData = async () => {
      setInitializationLoading(true);
      try {
        if (status === 'sent' || status === 'failed') {
          // For sent/failed expenses, fetch specific items
          const promises = [];
          if (recordToEdit?.dimension_id) {
            promises.push(fetchDimension());
          }
          if (recordToEdit?.project_id) {
            promises.push(fetchProject());
          }
          if (recordToEdit?.company_card_id) {
            promises.push(fetchCompanyCard());
          }
          if (recordToEdit?.xgl_id) {
            promises.push(fetchXgl());
          }
          await Promise.all(promises);
        } else {
          // For new/draft expenses, fetch collections
          if (status === '') {
            setInitializationLoading(false); //makes form usable sooner for new expenses
          }
          const promises = [
            fetchUserList(),
            fetchDimensionCollection(),
            fetchProjectCollection(),
            fetchCompanyCardCollection(),
            fetchXglCollection(),
            fetchCostTypeCollection(),
          ];
          await Promise.all(promises);
        }
      } finally {
        setInitializationLoading(false);
      }
    };

    initializeData();
  }, [
    status,
    recordToEdit,
    fetchDimension,
    fetchProject,
    fetchCompanyCard,
    fetchXgl,
    fetchUserList,
    fetchDimensionCollection,
    fetchProjectCollection,
    fetchCompanyCardCollection,
    fetchXglCollection,
    fetchCostTypeCollection,
  ]);

  return {
    // Overall initialization loading state
    initializationLoading,

    // Grouped by domain for better organization
    dimensionsData: {
      loading: dimensionsLoading,
      data: dimensions,
      onSearch: debouncedFetchDimensions,
      pagination: dimensionsPagination,
    },
    projectsData: {
      loading: projectsLoading,
      data: projects,
      onSearch: debouncedFetchProjects,
      pagination: projectsPagination,
    },
    costTypesData: {
      loading: costTypesLoading,
      data: costTypes,
      onSearch: debouncedFetchCostTypes,
      pagination: costTypesPagination,
    },
    companyCardsData: {
      loading: companyCardsLoading,
      data: companyCards,
      onSearch: debouncedFetchCompanyCards,
      pagination: companyCardsPagination,
    },
    xglData: {
      loading: xglListLoading,
      data: xglList,
      onSearch: debouncedFetchXgl,
      pagination: xglPagination,
    },
    usersData: {
      loading: userListLoading,
      data: userList,
      onSearch: null, // Users don't have search functionality
    },
  };
};
